import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import apiService from "../services/api";
import Button from "../components/ui/Button";
import PurchaseOrderForm from "../components/PurchaseOrderForm";
import toast from "react-hot-toast";
import { ShoppingCart, Trash2, ArrowLeft, Package } from "lucide-react";

export default function CustomerCart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    cart,
    loading: cartLoading,
    updateCartItem,
    removeFromCart,
    clearCart,
    cartCount,
  } = useCart();

  const [showPurchaseOrderForm, setShowPurchaseOrderForm] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  const getCartTotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  const handleCheckout = () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setShowPurchaseOrderForm(true);
  };

  const getCustomerInfo = (user) => ({
    id: user?.id,
    name: user?.name || "",
    companyName: user?.businessName || "",
    contactName: user?.contactName,
    contactRole: user?.contactRole,
    contactEmail: user?.contactEmail,
    contactMobile: user?.contactMobile,
    contactAltMobile: user?.contactAltMobile,
    billAddress: user?.billAddressLine || "",
    billCity: user?.billDistrict || "",
    billState: user?.billState || "",
    billZipCode: user?.billPinCode || "",
    billCountry: user?.billCountry || "India",
    billGpsLocation: user?.billGpsLocation,
    shipAddress: user?.shipAddressLine || "",
    shipCity: user?.shipDistrict || "",
    shipState: user?.shipState || "",
    shipZipCode: user?.shipPinCode || "",
    shipCountry: user?.shipCountry || "India",
    shipGpsLocation: user?.shipGpsLocation,
    businessCategory: user?.businessCategory,
    gstNumber: user?.gstNumber,
    panNumber: user?.panNumber,
    yearsInBusiness: user?.yearsInBusiness,
    address: user?.billAddressLine || "",
    district: user?.billDistrict || "",
    city: user?.billDistrict || "",
    state: user?.billState || "",
    zipCode: user?.billPinCode || "",
    country: user?.billCountry || "India",
    phone: user?.contactMobile || "",
    email: user?.email || "",
  });

  const handleSubmitOrder = async (formData) => {
    setCheckoutLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.gstPercent || 18,
        })),
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        status: "submitted",
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      const order = await apiService.createOrder(orderData);

      toast.success(
        `Purchase Order submitted successfully! PO #${
          order.poNumber || order.id
        } is pending approval`,
      );
      await clearCart();
      setShowPurchaseOrderForm(false);
      navigate("/customer/orders");
    } catch (error) {
      console.error("Failed to submit order:", error);
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConfirmPO = async (formData) => {
    setCheckoutLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.product?.gstPercent || 0,
        })),
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        status: "confirmed",
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      const order = await apiService.createOrder(orderData);

      await clearCart();
      setShowPurchaseOrderForm(false);

      toast.success(
        `Purchase Order created successfully! PO #${
          order.poNumber || order.id
        } is pending approval`,
      );
      navigate("/customer/orders");
    } catch (error) {
      console.error("Failed to confirm order:", error);
      toast.error("Failed to confirm order. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSaveDraft = async (formData) => {
    setDraftLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.gstPercent || 18,
        })),
        purchaseOrderNumber: formData.poNumber,
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      await apiService.saveDraft(orderData);
      toast.success("Draft saved successfully!");
      setShowPurchaseOrderForm(false);
      navigate("/customer/dashboard"); // Or wherever drafts are listed
    } catch (error) {
      console.error("Failed to save draft:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setDraftLoading(false);
    }
  };

  if (cartLoading && !cart) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/customer/products")}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              Shopping Cart
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {cartCount} items in your cart
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {cartCount > 0 && (
            <Button
              variant="outline"
              onClick={clearCart}
              className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900/30 dark:hover:bg-red-900/20"
            >
              Clear Cart
            </Button>
          )}
        </div>
      </div>

      {/* Cart Content */}
      {cart && cart.items && cart.items.length > 0 ? (
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items List */}
          <div className="flex-1 space-y-4">
            {cart.items.map((item) => (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:shadow-xl transition-shadow"
              >
                {/* Product Image */}
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  {item.productSku?.product?.imageUrl ? (
                    <img
                      src={item.productSku.product.imageUrl}
                      alt={item.productSku.product.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Package className="w-8 h-8 text-gray-400" />
                  )}
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {item.productSku?.product?.name || "Product"}
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                    <p>SKU: {item.productSku?.sku || "N/A"}</p>
                    {item.productSku?.name &&
                      item.productSku.name !==
                        item.productSku?.product?.name && (
                        <p>{item.productSku.name}</p>
                      )}
                  </div>
                </div>

                {/* Price & Quantity */}
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 w-full sm:w-auto">
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Price
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      ₹{item.price?.toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        updateCartItem(item.id, Math.max(1, item.quantity - 1))
                      }
                      className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold transition-colors"
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-medium text-gray-900 dark:text-white">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateCartItem(item.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold transition-colors"
                    >
                      +
                    </button>
                  </div>

                  <div className="text-right min-w-[80px]">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Total
                    </p>
                    <p className="font-bold text-blue-600 dark:text-blue-400">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>

                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="w-full lg:w-96">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sticky top-24 hover:shadow-xl transition-shadow">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Subtotal ({cartCount} items)</span>
                  <span>₹{getCartTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Tax (Estimated)</span>
                  <span>₹{(getCartTotal() * 0.18).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex justify-between font-bold text-lg text-gray-900 dark:text-white">
                  <span>Total</span>
                  <span>₹{(getCartTotal() * 1.18).toLocaleString()}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                variant="primary"
                className="w-full py-3 text-lg shadow-lg shadow-blue-500/25"
                loading={checkoutLoading}
              >
                Proceed to Checkout
              </Button>

              <Button
                onClick={() => navigate("/customer/products")}
                variant="outline"
                className="w-full mt-3"
              >
                Add More Items
              </Button>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                Taxes and shipping calculated at checkout
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
            <ShoppingCart className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your cart is empty
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Looks like you haven't added anything to your cart yet.
          </p>
          <Button
            onClick={() => navigate("/customer/products")}
            variant="primary"
            className="px-8"
          >
            Start Shopping
          </Button>
        </div>
      )}

      {/* Purchase Order Form Modal */}
      <PurchaseOrderForm
        isOpen={showPurchaseOrderForm}
        onClose={() => setShowPurchaseOrderForm(false)}
        cartItems={cart?.items || []}
        onSubmit={handleSubmitOrder}
        onConfirmPO={handleConfirmPO}
        onSaveDraft={handleSaveDraft}
        draftOrders={[]} // Not needed for checkout context usually, or fetch if needed
        draftLoading={draftLoading}
        customerInfo={getCustomerInfo(user)}
      />
    </div>
  );
}
